import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useFairTracker } from '../data/FairTrackerStore';
import { Field, Input, useToast } from '../ui';
import FairLogo from '../shell/FairLogo';
import { BASE } from '../routes';
import { tr } from '../i18n';

/**
 * First run (spec § 5.1).
 *
 * Blocked until Company and Your name are filled — those two feed the card the
 * buyer holds up at a booth, and a card without them is useless.
 *
 * Nothing is prefilled (see below). `createFirstFair` still reuses an existing
 * fair of the same name rather than duplicating it — this screen is reachable
 * again from Settings ("Preview first-run screen"), and re-running it must not
 * create the same fair twice.
 */
export default function WelcomeScreen() {
  const history = useHistory();
  const toast = useToast();
  const { settings, saveSettings, createFirstFair, setWelcomed } = useFairTracker();

  const [profile, setProfile] = useState(() => ({ ...((settings && settings.profile) || {}) }));

  /**
   * Nothing is prefilled — the buyer types their own details.
   *
   * DEVIATION from spec § 5.1, by product decision: the specification fills the
   * first fair in with "Canton Fair / Guangzhou, China" as a sample and pulls
   * the company card from the BeeNexa account. Both are off. The placeholders
   * below still show the expected shape without putting words in anyone's
   * mouth, which also removes the risk of a sample value being saved unnoticed.
   */
  const [fair, setFair] = useState({ name: '', location: '', dates: '' });
  const [saving, setSaving] = useState(false);

  const set = (k) => (v) => setProfile((p) => ({ ...p, [k]: v }));
  const setF = (k) => (v) => setFair((f) => ({ ...f, [k]: v }));

  // A fair name is required too now that nothing is prefilled — otherwise the
  // first fair would be created as "Untitled fair".
  const ready =
    !!(profile.company || '').trim() &&
    !!(profile.name || '').trim() &&
    !!(fair.name || '').trim();

  const start = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      await saveSettings({ ...settings, profile });
      const created = await createFirstFair(fair);
      await setWelcomed();
      history.push(created ? BASE : BASE);
    } catch (e) {
      toast('Could not save on this device');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ft-page ft-page-bare">
      <section className="ft-hero">
        <div className="ft-hero-brand">
          <FairLogo size={40} />
          <h1 className="ft-hero-h1">{tr("Fair Tracker")}</h1>
        </div>
        {/* One line on desktop; wraps naturally on a phone. No max-width, so
            the break is decided by the available space rather than forced. */}
        <p className="ft-hero-lead">
          {tr("Walk the show. Capture every supplier in two minutes. Decide at home with real numbers.")}
        </p>
      </section>

      <section className="ft-card">
        <h2 className="ft-h2">{tr("Your company card")}</h2>
        <p className="ft-sub">{tr("This is what you show suppliers at the booth.")}</p>

        <Field label={tr("Company *")}>
          <Input value={profile.company} onChange={set('company')} placeholder="NuVe System" />
        </Field>
        <Field label={tr("Your name *")}>
          <Input value={profile.name} onChange={set('name')} placeholder="Elizabeth Rodriguez" />
        </Field>
        <div className="ft-grid-2">
          <Field label={tr("Title")}>
            <Input value={profile.title} onChange={set('title')} placeholder="Founder & CEO" />
          </Field>
          <Field label={tr("Phone")}>
            <Input value={profile.phone} onChange={set('phone')} type="tel" />
          </Field>
          <Field label="WhatsApp" hint={tr("Digits only — country code first")}>
            <Input value={profile.whatsapp} onChange={set('whatsapp')} type="tel" />
          </Field>
          <Field label={tr("WeChat ID")}>
            <Input value={profile.wechat} onChange={set('wechat')} />
          </Field>
          <Field label={tr("Email")}>
            <Input value={profile.email} onChange={set('email')} type="email" />
          </Field>
          <Field label={tr("Website")}>
            <Input value={profile.website} onChange={set('website')} />
          </Field>
        </div>
        <Field label={tr("Address")}>
          <Input value={profile.address} onChange={set('address')} />
        </Field>
      </section>

      <section className="ft-card">
        <h2 className="ft-h2">{tr("Your first fair")}</h2>
        <p className="ft-sub">{tr("Which show are you going to?")}</p>
        <Field label={tr("Fair name")}>
          <Input value={fair.name} onChange={setF('name')} placeholder="Canton Fair" />
        </Field>
        <div className="ft-grid-2">
          <Field label={tr("City / country")}>
            <Input value={fair.location} onChange={setF('location')} placeholder="Guangzhou, China" />
          </Field>
          <Field label={tr("Dates")} hint={tr("However you like — it is just a label")}>
            <Input value={fair.dates} onChange={setF('dates')} placeholder="Oct 15–19, 2026" />
          </Field>
        </div>
      </section>

      <button
        type="button"
        className="ft-btn ft-btn-gold ft-btn-block"
        style={{ marginTop: 14 }}
        disabled={!ready || saving}
        onClick={start}
      >
        {saving ? 'Setting up…' : 'Create fair and start →'}
      </button>
      {!ready && (
        <p className="ft-hint" style={{ textAlign: 'center' }}>
          {tr("Add your company, your name and the fair name to continue.")}
        </p>
      )}

      <ol className="ft-ministeps">
        <li>
          <b>{tr("On the floor")}</b>
          <span>{tr("Quick Entry — photo, price, MOQ, rate, save.")}</span>
        </li>
        <li>
          <b>{tr("Back home")}</b>
          <span>{tr("Final Review — landed cost, certifications, risk.")}</span>
        </li>
        <li>
          <b>{tr("Decide")}</b>
          <span>{tr("Compare finalists and approve.")}</span>
        </li>
      </ol>

      <p className="ft-footnote">{tr("Fair Tracker · a BeeNexa product · works offline on the show floor")}</p>
    </div>
  );
}
